// src/services/ReportService.js

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { TimeEntryReport, formatFileDate } from '../models/TimeEntryReportModel';
import { VacationReportModel } from '../models/VacationReportModel';
import { SickLeaveReportModel } from '../models/SickLeaveReportModel';
import { ScheduleReportModel } from '../models/ScheduleReportModel';
import { ShiftModel } from '../models/ScheduleModels';

/**
 * Service for generating time tracking, vacation, schedule, and sick leave reports
 */
export class ReportService {
  /**
   * Load staff members for a department
   */
  static async loadStaffMembers(companyId, departmentId) {
    try {
      const staffRef = collection(db, 'companies', companyId, 'staff');
      const staffQuery = query(staffRef, where('departmentIds', 'array-contains', departmentId));
      const staffSnapshot = await getDocs(staffQuery);

      const staffMembers = [];
      staffSnapshot.forEach(doc => {
        staffMembers.push({
          id: doc.id,
          name: doc.data().name || 'Unknown Staff',
          active: doc.data().active !== false // archived staff still included, flagged here
        });
      });

      return staffMembers.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading staff members:', error);
      throw error;
    }
  }

  /**
   * Load company and department information
   */
  static async loadCompanyAndDepartmentInfo(companyId, departmentId) {
    try {
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      const companyName = companyDoc.exists() ? companyDoc.data().name : 'Unknown Company';

      const departmentDoc = await getDoc(doc(db, 'companies', companyId, 'departments', departmentId));
      const departmentName = departmentDoc.exists() ? departmentDoc.data().name : 'Unknown Department';

      return { companyName, departmentName };
    } catch (error) {
      console.error('Error loading company/department info:', error);
      throw error;
    }
  }

  /**
   * Load time entries for generating reports
   */
  static async loadTimeEntries({ companyId, staffIds, startDate, endDate, reportType, staffNames }) {
    try {
      const startDateString = formatFileDate(startDate);
      const endDateString = formatFileDate(endDate);

      let allEntries = [];

      for (const staffId of staffIds) {
        const staffName = staffNames[staffId] || `Staff Member (${staffId.substr(-4)})`;

        const entriesRef = collection(db, 'companies', companyId, 'timeEntries', staffId, 'entries');
        const entriesQuery = query(
          entriesRef,
          where('date', '>=', startDateString),
          where('date', '<=', endDateString)
        );

        const entriesSnapshot = await getDocs(entriesQuery);

        entriesSnapshot.forEach(entryDoc => {
          const entryData = entryDoc.data();

          if (!entryData.clockIns || entryData.clockIns.length === 0) {
            return;
          }

          if (reportType === 'All Time Entries') {
            // One row per session; sub-minute sessions are skipped by the model
            const sessionEntries = TimeEntryReport.sessionsFromFirestore(
              entryData,
              entryDoc.id,
              staffId,
              staffName
            );
            allEntries = [...allEntries, ...sessionEntries];
          } else if (reportType === 'Working Time Record') {
            // One row per day; same break source (totalBreakDuration) as Daily Summary.
            // Returns null when the day has no valid (>= 60 s) session.
            const dayEntry = TimeEntryReport.dayFromFirestore(
              entryData,
              entryDoc.id,
              staffId,
              staffName
            );
            if (dayEntry) allEntries.push(dayEntry);
          } else {
            const entry = TimeEntryReport.fromFirestore(
              entryData,
              entryDoc.id,
              staffId,
              staffName
            );
            allEntries.push(entry);
          }
        });
      }

      return this.processReportData(allEntries, reportType);
    } catch (error) {
      console.error('Error loading time entries:', error);
      throw error;
    }
  }

  /**
   * Load vacation entries for generating vacation reports.
   *
   * The date field in the vacations collection is stored as a YYYY-MM-DD string.
   * The query uses string comparison which matches the actual Firestore storage format.
   *
   * Important: Firestore performs strict type matching on range filter operands.
   * Querying a string field with Timestamp objects returns zero results silently.
   * String-to-string comparison is the correct approach here.
   *
   * durationMinutes is intentionally excluded - vacation reports show dates only.
   */
  static async loadVacationEntries({ companyId, departmentId, staffIds, startDate, endDate, staffMembers }) {
    try {
      // Format as YYYY-MM-DD strings to match how the date field is stored in Firestore
      const startDateString = formatFileDate(startDate);
      const endDateString = formatFileDate(endDate);

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const vacationsQuery = query(
        vacationsRef,
        where('departmentId', '==', departmentId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString)
      );

      const snapshot = await getDocs(vacationsQuery);
      const entries = [];

      snapshot.forEach(docSnap => {
        const data = docSnap.data();

        // Post-filter by selected staffIds in memory
        if (!staffIds.includes(data.staffId)) return;

        // Cross-reference staffName from the already-loaded staffMembers array.
        // staffName is not stored in Firestore on the vacation document.
        const staffMember = staffMembers.find(s => s.id === data.staffId);
        const staffName = staffMember
          ? staffMember.name
          : `Staff Member (${data.staffId.substr(-4)})`;

        // Resolve date to a YYYY-MM-DD string.
        // String is checked first since that is the current storage format.
        // Timestamp handling is kept as a defensive fallback.
        let dateString;
        if (typeof data.date === 'string') {
          dateString = data.date;
        } else if (data.date && typeof data.date.toDate === 'function') {
          const localDate = data.date.toDate();
          const year = localDate.getFullYear();
          const month = String(localDate.getMonth() + 1).padStart(2, '0');
          const day = String(localDate.getDate()).padStart(2, '0');
          dateString = `${year}-${month}-${day}`;
        } else {
          console.warn('Vacation entry has unrecognized date format:', docSnap.id, data.date);
          return;
        }

        entries.push(new VacationReportModel(
          data.staffId,
          staffName,
          dateString
        ));
      });

      // Sort by date ascending, then by staff name alphabetically
      return entries.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.staffName.localeCompare(b.staffName);
      });
    } catch (error) {
      console.error('Error loading vacation entries:', error);
      throw error;
    }
  }

  /**
   * Load sick leave entries for generating sick leave reports.
   *
   * The date field in the sickLeave collection is stored as a YYYY-MM-DD string,
   * confirmed by SickLeaveModel.toFirestore(). String-to-string range comparison
   * is the correct query approach — see loadVacationEntries for the same reasoning.
   *
   * Index strategy: query by date range only. departmentId and staffIds are
   * filtered in memory. The sickLeave collection has a single-field index on date
   * only, so including departmentId in the query would require a new composite
   * index. In-memory filtering is safe given the expected volume per date range.
   *
   * durationMinutes is intentionally excluded — sick leave reports show days only
   * for v1, consistent with the vacation report pattern.
   */
  static async loadSickLeaveEntries({ companyId, departmentId, staffIds, startDate, endDate, staffMembers }) {
    try {
      // Format as YYYY-MM-DD strings to match how the date field is stored in Firestore
      const startDateString = formatFileDate(startDate);
      const endDateString = formatFileDate(endDate);

      // --- Temporary debug logs - remove after confirming root cause ---
      console.log('[SickLeave] Query range:', startDateString, '→', endDateString);
      console.log('[SickLeave] Department filter:', departmentId);
      console.log('[SickLeave] Staff IDs filter:', staffIds);
      // -----------------------------------------------------------------

      const sickLeaveRef = collection(db, 'companies', companyId, 'sickLeave');

      // Query by date range only — departmentId is not included in the query
      // to avoid requiring a composite index on the sickLeave collection.
      const sickLeaveQuery = query(
        sickLeaveRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString)
      );

      const snapshot = await getDocs(sickLeaveQuery);

      // --- Temporary debug logs - remove after confirming root cause ---
      console.log('[SickLeave] Firestore snapshot size:', snapshot.size);
      snapshot.forEach(docSnap => {
        const d = docSnap.data();
        console.log('[SickLeave] Doc:', docSnap.id, {
          date: d.date,
          departmentId: d.departmentId,
          staffId: d.staffId,
          passedDeptFilter: d.departmentId === departmentId,
          passedStaffFilter: staffIds.includes(d.staffId)
        });
      });
      // -----------------------------------------------------------------

      const entries = [];

      snapshot.forEach(docSnap => {
        const data = docSnap.data();

        // Post-filter by departmentId in memory
        if (data.departmentId !== departmentId) return;

        // Post-filter by selected staffIds in memory
        if (!staffIds.includes(data.staffId)) return;

        // Cross-reference staffName from the already-loaded staffMembers array.
        // staffName is not stored in Firestore on the sick leave document.
        const staffMember = staffMembers.find(s => s.id === data.staffId);
        const staffName = staffMember
          ? staffMember.name
          : `Staff Member (${data.staffId.substr(-4)})`;

        // Resolve date to a YYYY-MM-DD string.
        // String is checked first since that is the current storage format.
        // Timestamp handling is kept as a defensive fallback.
        let dateString;
        if (typeof data.date === 'string') {
          dateString = data.date;
        } else if (data.date && typeof data.date.toDate === 'function') {
          const localDate = data.date.toDate();
          const year = localDate.getFullYear();
          const month = String(localDate.getMonth() + 1).padStart(2, '0');
          const day = String(localDate.getDate()).padStart(2, '0');
          dateString = `${year}-${month}-${day}`;
        } else {
          console.warn('Sick leave entry has unrecognized date format:', docSnap.id, data.date);
          return;
        }

        entries.push(new SickLeaveReportModel(
          data.staffId,
          staffName,
          dateString
        ));
      });

      // Sort by date ascending, then by staff name alphabetically
      return entries.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.staffName.localeCompare(b.staffName);
      });
    } catch (error) {
      console.error('Error loading sick leave entries:', error);
      throw error;
    }
  }

  /**
   * Load schedule shift entries for generating schedule reports
   */
  static async loadScheduleEntries({ companyId, departmentId, staffIds, startDate, endDate, staffMembers }) {
    try {
      const startDateString = formatFileDate(startDate);
      const endDateString = formatFileDate(endDate);

      let allEntries = [];

      for (const staffId of staffIds) {
        const staffMember = staffMembers.find(s => s.id === staffId);
        const staffName = staffMember
          ? staffMember.name
          : `Staff Member (${staffId.substr(-4)})`;

        const shiftsRef = collection(db, 'companies', companyId, 'schedules', staffId, 'shifts');
        const shiftsQuery = query(
          shiftsRef,
          where('date', '>=', startDateString),
          where('date', '<=', endDateString)
        );

        const snapshot = await getDocs(shiftsQuery);

        snapshot.forEach(docSnap => {
          const shift = ShiftModel.fromFirestore(docSnap);

          // Post-filter by departmentId in memory
          if (shift.departmentId !== departmentId) return;

          allEntries.push(ScheduleReportModel.fromShiftModel(shift, staffId, staffName));
        });
      }

      // Sort by date ascending, then by staff name alphabetically
      return allEntries.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return a.staffName.localeCompare(b.staffName);
      });
    } catch (error) {
      console.error('Error loading schedule entries:', error);
      throw error;
    }
  }

  /**
   * Process raw report data based on report type
   */
  static processReportData(entries, reportType) {
    switch (reportType) {
      case 'All Time Entries':
        return entries.sort((a, b) => a.date - b.date);

      // Working Time Record and Daily Summary share the same per-staff/per-day
      // merge: min clockIn, max clockOut, summed break and presence. For Working
      // Time Record this also merges several entry docs that fall on one day.
      case 'Working Time Record':
      case 'Daily Summary':
        const dailySummaries = {};

        for (const entry of entries) {
          const dateKey = formatFileDate(entry.date);
          const key = `${dateKey}-${entry.staffId}`;

          if (dailySummaries[key]) {
            const existingEntry = dailySummaries[key];

            existingEntry.clockIn = new Date(Math.min(
              existingEntry.clockIn.getTime(),
              entry.clockIn.getTime()
            ));

            existingEntry.clockOut = new Date(Math.max(
              existingEntry.clockOut.getTime(),
              entry.clockOut.getTime()
            ));

            existingEntry.breakDuration += entry.breakDuration;
            existingEntry.presenceDuration += entry.presenceDuration;
          } else {
            dailySummaries[key] = new TimeEntryReport(
              entry.id,
              entry.staffId,
              entry.staffName,
              entry.date,
              entry.clockIn,
              entry.clockOut,
              entry.breakDuration,
              entry.presenceDuration,
              entry.sessionId,
              entry.departmentId,
              entry.departmentName
            );
          }
        }

        return Object.values(dailySummaries).sort((a, b) => a.date - b.date);

      default:
        return entries;
    }
  }

  /**
   * Aggregate schedule entries by ISO week and staff for Weekly Summary type.
   *
   * The grouping key includes weekYear to correctly handle ISO week boundaries
   * where week 1 of one year must not be merged with week 1 of another year.
   * weekYear may differ from the calendar year at year boundaries - for example,
   * 2024-12-30 belongs to ISO week year 2025.
   */
  static aggregateScheduleByWeek(reportData) {
    const weekMap = {};

    reportData.forEach(entry => {
      const key = `${entry.weekYear}-${entry.weekNumber}-${entry.staffId}`;

      if (!weekMap[key]) {
        weekMap[key] = {
          staffId: entry.staffId,
          staffName: entry.staffName,
          weekNumber: entry.weekNumber,
          weekYear: entry.weekYear,
          shiftCount: 0,
          totalMinutes: 0
        };
      }

      weekMap[key].shiftCount++;
      weekMap[key].totalMinutes += entry.totalMinutes;
    });

    return Object.values(weekMap).sort((a, b) => {
      if (a.weekYear !== b.weekYear) return a.weekYear - b.weekYear;
      if (a.weekNumber !== b.weekNumber) return a.weekNumber - b.weekNumber;
      return a.staffName.localeCompare(b.staffName);
    });
  }

  /**
   * Calculate totals for time tracking reports
   */
  static calculateReportTotals(reportData) {
    const totalPresence = reportData.reduce((sum, entry) => sum + entry.presenceDuration, 0);
    const totalBreak = reportData.reduce((sum, entry) => sum + entry.breakDuration, 0);
    const totalWork = reportData.reduce((sum, entry) => sum + entry.workDuration, 0);

    return { totalPresence, totalBreak, totalWork };
  }

  /**
   * Calculate totals for vacation reports.
   * Returns total vacation days only. Duration is not used in vacation reports.
   */
  static calculateVacationTotals(reportData) {
    return { totalDays: reportData.length };
  }

  /**
   * Calculate totals for sick leave reports.
   * Returns total sick days only. Duration is not used in sick leave reports (v1).
   * Each entry in reportData represents exactly one sick day document.
   */
  static calculateSickLeaveTotals(reportData) {
    return { totalDays: reportData.length };
  }

  /**
   * Calculate totals for schedule reports
   */
  static calculateScheduleTotals(reportData) {
    const totalMinutes = reportData.reduce((sum, e) => sum + (e.totalMinutes || 0), 0);
    return { totalMinutes };
  }

  /**
   * Get all staff names by IDs
   */
  static async getStaffNames(companyId, staffIds) {
    const staffNames = {};

    for (let i = 0; i < staffIds.length; i += 10) {
      const batch = staffIds.slice(i, i + 10);

      try {
        const staffRef = collection(db, 'companies', companyId, 'staff');
        const q = query(staffRef);
        const querySnapshot = await getDocs(q);

        querySnapshot.forEach(doc => {
          if (batch.includes(doc.id)) {
            staffNames[doc.id] = doc.data().name || `Staff Member (${doc.id.substr(-4)})`;
          }
        });
      } catch (error) {
        console.error(`Error fetching staff batch ${i}:`, error);
      }
    }

    return staffNames;
  }

  /**
   * Generate CSV content from report data.
   * Branches by reportType to handle time tracking, vacation, schedule,
   * and sick leave formats.
   */
  static generateCSV(reportData, reportType, t) {
    const headers = this.getCSVHeaders(reportType, t);
    const rows = [headers];

    if (reportType === 'Weekly Summary') {
      const aggregated = this.aggregateScheduleByWeek(reportData);
      aggregated.forEach(entry => rows.push(this.getCSVRow(entry, reportType, t)));
      const grandTotal = {
        totalMinutes: aggregated.reduce((sum, e) => sum + e.totalMinutes, 0)
      };
      rows.push(this.getCSVTotalsRow(grandTotal, reportType, t));

    } else if (reportType === 'Vacation Overview') {
      reportData.forEach(entry => rows.push(this.getCSVRow(entry, reportType, t)));
      const totals = this.calculateVacationTotals(reportData);
      rows.push(this.getCSVTotalsRow(totals, reportType, t));

    } else if (reportType === 'Sick Leave Overview') {
      reportData.forEach(entry => rows.push(this.getCSVRow(entry, reportType, t)));
      const totals = this.calculateSickLeaveTotals(reportData);
      rows.push(this.getCSVTotalsRow(totals, reportType, t));

    } else if (reportType === 'Schedule Overview') {
      reportData.forEach(entry => rows.push(this.getCSVRow(entry, reportType, t)));
      const totals = this.calculateScheduleTotals(reportData);
      rows.push(this.getCSVTotalsRow(totals, reportType, t));

    } else {
      // Time tracking types: Working Time Record, All Time Entries, Daily Summary
      reportData.forEach(entry => rows.push(this.getCSVRow(entry, reportType, t)));
      const totals = this.calculateReportTotals(reportData);
      rows.push(this.getCSVTotalsRow(totals, reportType, t));
    }

    return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
  }

  /**
   * Get CSV headers based on report type.
   * Vacation and sick leave reports show Date and Staff only — no duration columns.
   *
   * Working Time Record: one row per staff per day with first clock-in, last
   * clock-out, Anwesenheit, Pausenzeit and Arbeitszeit (same figures as Daily Summary).
   *
   * All Time Entries: the value column is Anwesenheit (presence = clockOut − clockIn
   * per session), matching the Start/End columns shown in the report and the
   * Daily Summary Anwesenheit total. Net work time is the job of Daily Summary.
   */
  static getCSVHeaders(reportType, t) {
    switch (reportType) {
      case 'Working Time Record':
        return [
          t('date'),
          t('staff'),
          t('clockIn'),
          t('clockOut'),
          t('presenceDuration'),
          t('breakTime'),
          t('workDuration')
        ];
      case 'All Time Entries':
        // presenceDuration key maps to "Presence" (EN) / "Anwesenheit" (DE),
        // matching what this column now shows: raw clockOut − clockIn per session.
        return [
          t('date'),
          t('staff'),
          t('startTime'),
          t('endTime'),
          t('presenceDuration')
        ];
      case 'Daily Summary':
        return [
          t('date'),
          t('staff'),
          t('presenceDuration'),
          t('breakTime'),
          t('workDuration')
        ];
      case 'Vacation Overview':
        return [
          t('date'),
          t('staff')
        ];
      case 'Sick Leave Overview':
        return [
          t('date'),
          t('staff')
        ];
      case 'Schedule Overview':
        return [
          t('date'),
          t('staff'),
          t('shiftStart'),
          t('shiftEnd'),
          t('scheduledHours')
        ];
      case 'Weekly Summary':
        return [
          t('calendarWeek'),
          t('staff'),
          t('scheduledHours')
        ];
      default:
        return [
          t('date'),
          t('staff'),
          t('presenceDuration'),
          t('breakTime'),
          t('workDuration')
        ];
    }
  }

  /**
   * Get CSV row data based on report type.
   * Vacation and sick leave rows contain date and staff name only.
   *
   * All Time Entries rows surface presenceDuration (clockOut − clockIn) so
   * the value column ties exactly to the Start/End columns on the same row.
   */
  static getCSVRow(entry, reportType, t) {
    // For JS Date objects (time tracking entries)
    const formatDate = (date) => {
      const d = new Date(date);
      const day = d.getDate().toString().padStart(2, '0');
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    // For YYYY-MM-DD strings (vacation, sick leave, and schedule entries).
    // Avoids UTC midnight timezone offset issues from new Date('YYYY-MM-DD').
    const formatDateFromString = (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      return `${day}.${month}.${year}`;
    };

    const formatTime = (date) => {
      const d = new Date(date);
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const formatDuration = (seconds) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    };

    const formatMinutes = (minutes) => {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${h}h ${m}m`;
    };

    switch (reportType) {
      case 'Working Time Record':
        return [
          formatDate(entry.date),
          entry.staffName,
          formatTime(entry.clockIn),
          formatTime(entry.clockOut),
          formatDuration(entry.presenceDuration),
          formatDuration(entry.breakDuration),
          formatDuration(entry.workDuration)
        ];
      case 'All Time Entries':
        // Presence (clockOut − clockIn) so the duration column matches the
        // Start/End columns on the same row. Net work is Daily Summary's role.
        return [
          formatDate(entry.date),
          entry.staffName,
          formatTime(entry.clockIn),
          formatTime(entry.clockOut),
          formatDuration(entry.presenceDuration)
        ];
      case 'Daily Summary':
        return [
          formatDate(entry.date),
          entry.staffName,
          formatDuration(entry.presenceDuration),
          formatDuration(entry.breakDuration),
          formatDuration(entry.workDuration)
        ];
      case 'Vacation Overview':
        return [
          formatDateFromString(entry.date),
          entry.staffName
        ];
      case 'Sick Leave Overview':
        return [
          formatDateFromString(entry.date),
          entry.staffName
        ];
      case 'Schedule Overview':
        return [
          formatDateFromString(entry.date),
          entry.staffName,
          entry.startTime,
          entry.endTime,
          formatMinutes(entry.totalMinutes)
        ];
      case 'Weekly Summary':
        return [
          `KW ${entry.weekNumber}`,
          entry.staffName,
          formatMinutes(entry.totalMinutes)
        ];
      default:
        return [
          formatDate(entry.date),
          entry.staffName,
          formatDuration(entry.presenceDuration),
          formatDuration(entry.breakDuration),
          formatDuration(entry.workDuration)
        ];
    }
  }

  /**
   * Get CSV totals row.
   * Vacation and sick leave totals show day counts only — no duration values.
   *
   * All Time Entries totals use totalPresence so the grand total matches the
   * Daily Summary Anwesenheit total for the same period.
   */
  static getCSVTotalsRow(totals, reportType, t) {
    const formatDuration = (seconds) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    };

    const formatMinutes = (minutes) => {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return `${h}h ${m}m`;
    };

    switch (reportType) {
      case 'Working Time Record':
        return [
          t('total'),
          t('allStaff'),
          '',
          '',
          formatDuration(totals.totalPresence),
          formatDuration(totals.totalBreak),
          formatDuration(totals.totalWork)
        ];
      case 'All Time Entries':
        // Total presence so the grand total aligns with Daily Summary
        // Anwesenheit (110:17 in the example, not 103:32).
        return [
          t('total'),
          t('allStaff'),
          '',
          '',
          formatDuration(totals.totalPresence)
        ];
      case 'Daily Summary':
        return [
          t('total'),
          t('allStaff'),
          formatDuration(totals.totalPresence),
          formatDuration(totals.totalBreak),
          formatDuration(totals.totalWork)
        ];
      case 'Vacation Overview':
        return [
          t('total'),
          `${totals.totalDays} ${t('vacationDays')}`
        ];
      case 'Sick Leave Overview':
        return [
          t('total'),
          `${totals.totalDays} ${t('sickDays')}`
        ];
      case 'Schedule Overview':
        return [
          t('total'),
          t('allStaff'),
          '',
          '',
          formatMinutes(totals.totalMinutes)
        ];
      case 'Weekly Summary':
        return [
          t('total'),
          t('allStaff'),
          formatMinutes(totals.totalMinutes)
        ];
      default:
        return [
          t('total'),
          t('allStaff'),
          formatDuration(totals.totalPresence),
          formatDuration(totals.totalBreak),
          formatDuration(totals.totalWork)
        ];
    }
  }
}

// ─── Report Category and Type Definitions ────────────────────────────────────

export const REPORT_CATEGORIES = [
  { id: 'time-tracking', labelKey: 'timeTracking' },
  { id: 'vacation',      labelKey: 'vacation'      },
  { id: 'schedule',      labelKey: 'schedule'      },
  { id: 'sick-leave',    labelKey: 'sickLeave'     }
];

export const REPORT_TYPES = [
  { id: 'Working Time Record',  labelKey: 'workingTimeRecord',     category: 'time-tracking' },
  { id: 'All Time Entries',     labelKey: 'allTimeEntries',        category: 'time-tracking' },
  { id: 'Daily Summary',        labelKey: 'dailySummary',          category: 'time-tracking' },
  { id: 'Vacation Overview',    labelKey: 'vacationOverview',      category: 'vacation'      },
  { id: 'Schedule Overview',    labelKey: 'scheduleOverview',      category: 'schedule'      },
  { id: 'Weekly Summary',       labelKey: 'weeklyScheduleSummary', category: 'schedule'      },
  { id: 'Sick Leave Overview',  labelKey: 'sickLeaveOverview',     category: 'sick-leave'    }
];